import { useEffect, useRef, useState } from 'react';

export interface PageHeaderControls {
  id: string;
  view: string;
  title: string;
  createLabel?: string;
  search?: { value: string; label: string; placeholder: string };
  filters: Array<{ id: string; label: string; value: string; options: Array<{ value: string; label: string }> }>;
}
export type PageHeaderControlAction = { type: 'create' } | { type: 'search'; value: string }
  | { type: 'filter'; id: string; value: string };
const STATE = 'page-header:controls';
const CLEAR = 'page-header:clear-controls';
const REQUEST = 'page-header:request-controls';
const ACTION = 'page-header:control-action';

export function usePublishPageHeaderControls(state: PageHeaderControls | null, onAction: (action: PageHeaderControlAction) => void): void {
  const actionRef = useRef(onAction);
  actionRef.current = onAction;
  useEffect(() => {
    if (!state) return;
    const action = (event: Event) => {
      const detail = (event as CustomEvent<{ id: string; action: PageHeaderControlAction }>).detail;
      if (detail.id === state.id) actionRef.current(detail.action);
    };
    const publish = () => window.dispatchEvent(new CustomEvent(STATE, { detail: state }));
    window.addEventListener(ACTION, action);
    window.addEventListener(REQUEST, publish);
    publish();
    return () => {
      window.removeEventListener(ACTION, action);
      window.removeEventListener(REQUEST, publish);
      window.dispatchEvent(new CustomEvent(CLEAR, { detail: state.id }));
    };
  }, [state]);
}
export function usePageHeaderControls(): PageHeaderControls | null {
  const [state, setState] = useState<PageHeaderControls | null>(null);
  useEffect(() => {
    const change = (event: Event) => setState((event as CustomEvent<PageHeaderControls | null>).detail);
    const clear = (event: Event) => setState((current) => current?.id === (event as CustomEvent<string>).detail ? null : current);
    window.addEventListener(STATE, change);
    window.addEventListener(CLEAR, clear);
    window.dispatchEvent(new Event(REQUEST));
    return () => { window.removeEventListener(STATE, change); window.removeEventListener(CLEAR, clear); };
  }, []);
  return state;
}
export function dispatchPageHeaderControl(id: string, action: PageHeaderControlAction): void {
  window.dispatchEvent(new CustomEvent(ACTION, { detail: { id, action } }));
}
